import { useState } from "react";
import { useParams } from "react-router-dom";
import { Box, ListItemText, Menu, MenuItem, Tooltip, Typography } from "@mui/material";
import { Avatar } from "@/components/elements/Avatar";
import { findLabelByValue } from "@/utils/object";
import { IconAssignment, IconChevronDown, IconInfoOutline, IconScan, IconSocialShare } from "@/assets/icons";
import { dayjs } from "@/utils/dayjs";
import { DATE_FORMAT } from "@/constants/datetime";
import { TTalentForm } from "@/features/talent/types/talentForm";
import { useEnum } from "@/api/services/global/enum";
import { FCC } from "@/types/common";
import { text } from "@/theme/colors";
import { TalentStatusTag } from "@/features/talent/components/talent-common/TalentStatusTag";
import { ERecruitmentStatus } from "@/types/enum";
import { EMPTY_STR } from "@/constants/string";
import { TALENT_WIDTH_FULL } from "@/features/talent/constants/width";
import { HIDE_PERSONAL_INFO_RESUME_MESSAGE } from "@/features/talent/constants/common";
import { Button } from "@/components/elements/Button";
import { downloadSingleResumeApi } from "@/api/services/main/talent";
import { showError } from "@/utils/error";
import { saveAs } from "file-saver";
import { ShareEnterpriseURLModal } from "@/components/elements/Modal/ShareEnterpriseURLModal";
import { ScanImageTalentModal } from "@/features/talent/components/talent-scan-image/ScanImageTalentModal";
import { useVisible } from "@/hooks/useVisible";
import { useSharingInfo } from "@/api/services/main/sharing";
import { SHARE_TYPE } from "@/api/services/main/sharing/dtos/sharing.dto";

type TTalentProfileHeaderProps = {
  defaultValues?: Partial<TTalentForm>;
  detailPage?: boolean;
};

export const TalentProfileHeader: FCC<TTalentProfileHeaderProps> = ({ defaultValues, detailPage = false }) => {
  const { talentId } = useParams();
  const { data: recruitmentStatusOptions } = useEnum("ERecruitmentStatus");
  const shareModal = useVisible();
  const scanImageTalentModal = useVisible();
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);

  const { data: sharingFull, refetch: refetchSharingFull } = useSharingInfo("talent", talentId, SHARE_TYPE.FULL);
  const { data: sharingMasked, refetch: refetchSharingMasked } = useSharingInfo("talent", talentId, SHARE_TYPE.MASKED);

  const refetchSharing = () => {
    refetchSharingFull();
    refetchSharingMasked();
  };

  const handleGenerateResume = async (masked?: boolean) => {
    if (!defaultValues?.id) return;

    try {
      const response = await downloadSingleResumeApi(defaultValues.id, masked);
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const suffix = masked ? "（マスキング）" : "";
      const displayName = masked && defaultValues.fullName
        ? defaultValues.fullName.trim().split(/\s+/).map((p: string) => p.charAt(0).toUpperCase()).join(".")
        : (defaultValues.fullName ?? defaultValues.nickName);
      const fileName = `${displayName}の履歴書${suffix}.xlsx`;
      saveAs(blob, fileName);
    } catch (error) {
      showError(error);
    }
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", mb: 3, width: `${TALENT_WIDTH_FULL}px` }}>
      <Avatar filePath={defaultValues?.avatarFilePath} width={100} height={133} />
      <Box display="flex" alignItems="center" justifyContent="space-between" ml={3} sx={{ flex: 1 }}>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1, flexWrap: "wrap" }}>
            <Typography
              variant="head20Semi"
              sx={{
                mr: 2,
                whiteSpace: "normal",
                wordBreak: "break-word",
                maxWidth: "60%",
              }}
            >
              {defaultValues?.fullName ? defaultValues.fullName.toUpperCase() : EMPTY_STR.TEXT}
            </Typography>

            <TalentStatusTag
              status={defaultValues?.recruitmentStatus ?? ERecruitmentStatus.DRAFT}
              title={findLabelByValue(recruitmentStatusOptions, defaultValues?.recruitmentStatus)}
              variant="bold"
            />
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body14" color={text.secondary} sx={{ display: "flex", alignItems: "center" }}>
              登録日:
              <span style={{ marginLeft: 4 }}>{defaultValues?.createdAt ? dayjs(defaultValues.createdAt).format(DATE_FORMAT) : ""}</span>
            </Typography>
            <Typography variant="body14" color={text.secondary} sx={{ display: "flex", alignItems: "center" }}>
              |
            </Typography>
            <Typography variant="body14" color={text.secondary} sx={{ display: "flex", alignItems: "center" }}>
              更新日:
              <span style={{ marginLeft: 4 }}>{defaultValues?.updatedAt ? dayjs(defaultValues.updatedAt).format(DATE_FORMAT) : ""}</span>
            </Typography>
          </Box>
        </Box>

        {detailPage ? (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Button
                type="button"
                variant="outline"
                size="sm"
                startIcon={<IconScan />}
                sx={{ fontWeight: 500, width: "228px" }}
                onClick={() => scanImageTalentModal.open()}
              >
                人材情報や履歴書のAI取込
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                startIcon={<IconAssignment />}
                endIcon={<IconChevronDown />}
                sx={{ fontWeight: 500, width: "206px" }}
                onClick={(e) => setMenuAnchorEl(e.currentTarget)}
              >
                履歴書を出力/共有
              </Button>
            </Box>
            <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={() => setMenuAnchorEl(null)}>
              <MenuItem
                onClick={() => {
                  setMenuAnchorEl(null);
                  handleGenerateResume();
                }}
              >
                <ListItemText>
                  <Typography variant="body14">履歴書を出力</Typography>
                </ListItemText>
              </MenuItem>
              {/* Masked resume output — hides personal info (name→initials, DOB, phone, email, address, photo) */}
              <MenuItem
                onClick={() => {
                  setMenuAnchorEl(null);
                  handleGenerateResume(true);
                }}
              >
                <ListItemText>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography variant="body14">マスキングした履歴書を出力</Typography>
                    <Tooltip
                      title={
                        <Typography variant="body14" sx={{ whiteSpace: "pre-line" }}>
                          {HIDE_PERSONAL_INFO_RESUME_MESSAGE}
                        </Typography>
                      }
                      arrow
                      placement="right"
                    >
                      <Box sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                        <IconInfoOutline width={16} height={16} color="#767B7E" />
                      </Box>
                    </Tooltip>
                  </Box>
                </ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setMenuAnchorEl(null);
                  shareModal.open();
                }}
              >
                <ListItemText>
                  <Typography variant="body14">企業にURLを共有</Typography>
                </ListItemText>
              </MenuItem>
            </Menu>
            <ShareEnterpriseURLModal
              modal={shareModal}
              title="企業への人材情報共有"
              description="企業に共有するURL、パスワードをこちらから発行できます。"
              entityType="talent"
              entityId={talentId}
              shareType={SHARE_TYPE.FULL}
              shareLinks={{
                full: {
                  shareUrl: sharingFull?.shareUrl ?? null,
                  sharePassword: sharingFull?.sharePassword ?? null,
                  shareUrlExpiredAt: sharingFull?.shareUrlExpiredAt ?? null,
                },
                masked: {
                  shareUrl: sharingMasked?.shareUrl ?? null,
                  sharePassword: sharingMasked?.sharePassword ?? null,
                  shareUrlExpiredAt: sharingMasked?.shareUrlExpiredAt ?? null,
                },
              }}
              onGenerateSuccess={() => refetchSharing()}
              showShareTypeSelector
              maskingTooltipText={"一部マスキングする履歴書情報は\n氏名、呼び名、生年月日、電話番号、メールアドレス、現住所、本人顔写真となります。"}
            />
            <ScanImageTalentModal modal={scanImageTalentModal} currentTalent={(defaultValues as TTalentForm | undefined) ?? null} />
          </>
        ) : null}
      </Box>
    </Box>
  );
};
